import { computed, Signal } from '@angular/core';
import { Permission } from '../../api/app-types/permission.app';
import { PERMISSION_RESOURCE } from './permissions.types';

/**
 * Checks if a user has a certain permission or all permissions from a list.
 * @example
 * private readonly session = inject(SessionManagerService);
 *
 * const canCreateUser = hasPermission(this.session.permissions, 'User.Create');
 * @param userPermissions A signal with all permissions of the user
 * @param permission the permission(s) to check if user has access to
 * @returns a computed signal that returns true if user has the permission(s), false otherwise
 */
export function hasPermission(
	userPermissions: Signal<Permission[]>,
	permission: PERMISSION_RESOURCE | PERMISSION_RESOURCE[],
): Signal<boolean> {
	if (!userPermissions) {
		throw new Error('userPermissions signal is required');
	}
	// if no permission is provided, return true
	if (!permission || (Array.isArray(permission) && permission.length === 0)) {
		return computed(() => true);
	}
	// Return a computed signal that updates when userPermissions or permission changes
	return computed(() => {
		let hasPermission = false;
		if (typeof permission === 'string') {
			hasPermission = userPermissions().some((p) => p.name.toLowerCase() === permission.toLowerCase());
		} else if (Array.isArray(permission)) {
			hasPermission = permission.some((perm) =>
				userPermissions().some((p) => p.name.toLowerCase() === perm.toLowerCase()),
			);
		}

		return hasPermission;
	});
}
